/*
 * @Description: stape sGTM 工具集
 *   - stapeTrack：将神策事件转换为 GA4 规范参数后推送至 stapeDataLayer
 * @FilePath: /common/stape.ts
 */

type StapeUserData = {
  user_id?: number;
  email?: string;
  phone_number?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  country?: string;
  gender?: string;
};

type StapeTrackParams = Record<string, unknown>;

type StapeDataLayerPayload = {
  event: string;
  user_data: StapeUserData;
  [key: string]: unknown;
};

declare global {
  interface Window {
    stapeDataLayer?: {
      push: (payload: StapeDataLayerPayload) => number;
    };
  }
}

// ─── GA4 类型定义 ────────────────────────────────────────────────────────────

type GA4Item = {
  item_id?: string;
  item_name?: string;
  item_category?: string;
  price?: number;
  quantity?: number;
  item_product_id?: string;
};

type GA4Payload = {
  event: string;
  currency?: string;
  value?: number;
  items?: GA4Item[];
  transaction_id?: string;
  shipping?: number;
  tax?: number;
  payment_type?: string;
  search_term?: string;
  item_list_name?: string;
  method?: string;
};

// ─── 用户数据 ─────────────────────────────────────────────────────────────────

/**
 * 从单个 name 字段中提取 first_name 和 last_name
 * 无空格时 first_name = last_name = 整个名字
 */
function splitName(name: string): { first_name: string; last_name: string } {
  const trimmed = name.trim();
  const spaceIndex = trimmed.indexOf(' ');
  if (spaceIndex === -1) {
    return { first_name: trimmed, last_name: trimmed };
  }
  return {
    first_name: trimmed.substring(0, spaceIndex).trim(),
    last_name: trimmed.substring(spaceIndex + 1).trim(),
  };
}

/**
 * 将生日格式从 MM/DD/YYYY 转换为 YYYYMMDD
 * stape data-client 要求的标准格式
 */
function formatBirthday(birthday: string): string | null {
  try {
    const parts = birthday.split('/');
    if (parts.length !== 3) return null;
    const [month, day, year] = parts;
    return `${year}${month.padStart(2, '0')}${day.padStart(2, '0')}`;
  } catch {
    return null;
  }
}

/**
 * 从 localStorage 获取完整用户信息，用于拼装 user_data
 * stape data-client 服务端会对原始值进行 SHA256 加密后再转发给各广告平台
 * 因此此处传入明文原始值，不在客户端做哈希
 */
function getStapeUserData(): StapeUserData {
  try {
    const userInfoStr = localStorage.getItem('voo-userInfo');
    if (!userInfoStr) return {};
    const info = JSON.parse(userInfoStr) as {
      id?: number;
      email?: string;
      telephone?: string;
      tel_code?: string;
      name?: string;
      firstname?: string;
      lastname?: string;
      country_id?: string;
      gender?: number;
      birthday?: string;
    };

    let first_name = info.firstname || undefined;
    let last_name = info.lastname || undefined;
    if (info.name && (!first_name || !last_name)) {
      const split = splitName(info.name);
      first_name = first_name || split.first_name || undefined;
      last_name = last_name || split.last_name || undefined;
    }

    const gender = info.gender === 1 ? 'm' : info.gender === 2 ? 'f' : undefined;

    const date_of_birth = info.birthday ? (formatBirthday(info.birthday) ?? undefined) : undefined;

    return {
      user_id: info.id || undefined,
      email: info.email || undefined,
      phone_number: info.telephone
        ? info.tel_code
          ? `${info.tel_code}${info.telephone}`
          : info.telephone
        : undefined,
      first_name,
      last_name,
      country: info.country_id?.toLowerCase() || undefined,
      gender,
      date_of_birth,
    };
  } catch {
    return {};
  }
}

// ─── 白名单 ───────────────────────────────────────────────────────────────────

const STAPE_EVENT_WHITELIST = new Set([
  'IsReturn_Result',
  'ProductDetailsPage_View',
  'ProductDetailsPageBtn_Click',
  'AddToCart_Result',
  'CollectBtn_Click',
  'PlaceOrderPage_View',
  'PlaceOrderPagePlaceOrder_Result',
  'Purchase',
  'LoginSuccessNew_Result',
  'LoginSuccess_Result',
  'PageView', // 页面浏览
]);

const PRODUCT_DETAILS_BTN_NAME_WHITELIST = new Set(['Frame Only', 'Select Lenses']);

function isAllowedStapeEvent(eventName: string, params: StapeTrackParams = {}): boolean {
  if (!STAPE_EVENT_WHITELIST.has(eventName)) return false;

  if (eventName === 'ProductDetailsPageBtn_Click') {
    return PRODUCT_DETAILS_BTN_NAME_WHITELIST.has(String(params?.btn_name ?? ''));
  }

  // Reorder 场景缺少商品信息，转发到 Stape 后会导致 Facebook CAPI 400 错误
  if (eventName === 'AddToCart_Result' && params.add_cart_scene === 'Reorder加购') {
    return false;
  }

  return true;
}

// ─── GA4 子转换函数 ───────────────────────────────────────────────────────────

function convertSearchBtnClick(params: StapeTrackParams): GA4Payload {
  return {
    event: 'search',
    search_term: String(params.search_text ?? ''),
    items: (params.items as GA4Item[] | undefined) ?? [],
  };
}

function convertProductDetailsPageView(params: StapeTrackParams): GA4Payload {
  return {
    event: 'view_item',
    currency: String(params.currency ?? ''),
    value: Number(params.current_product_price ?? 0),
    items: [
      {
        item_product_id: String(params.product_id ?? ''),
        item_id: String(params.shop_sku ?? ''),
        item_name: String(params.product_name ?? ''),
        item_category: String(params.product_class_name ?? ''),
        price: Number(params.current_product_price ?? 0),
        quantity: 1,
      },
    ],
  };
}

function convertProductDetailsPageBtnClick(params: StapeTrackParams): GA4Payload {
  return {
    event: params.btn_name === 'Frame Only' ? 'frameOnlyC' : 'selectLens',
  };
}

function convertAddToCartBtnClick(params: StapeTrackParams): GA4Payload {
  return {
    event: 'add_to_cart',
    currency: String(params.currency ?? ''),
    value: Number(params.current_product_price ?? 0),
    items: [
      {
        item_product_id: String(params.product_id ?? ''),
        item_id: String(params.shop_sku ?? ''),
        item_name: String(params.product_name ?? ''),
        item_category: String(params.product_class_name ?? ''),
        price: Number(params.current_product_price ?? 0),
        quantity: 1,
      },
    ],
  };
}

function convertCollectBtnClick(params: StapeTrackParams): GA4Payload {
  const currency = JSON.parse(localStorage.getItem('voo-currency') || '{}').name || 'USD';
  return {
    event: 'add_to_wishlist',
    currency,
    value: Number(params.current_product_price ?? 0),
    items: [
      {
        item_product_id: String(params.product_id ?? ''),
        item_id: String(params.shop_sku ?? ''),
        item_name: String(params.product_name ?? ''),
      },
    ],
  };
}

function convertPlaceOrderPageView(_params: StapeTrackParams): GA4Payload {
  const currency = JSON.parse(localStorage.getItem('voo-currency') || '{}').name || 'USD';
  return {
    event: 'generate_lead',
    currency,
    // generate_lead 不关联具体商品，不传 items，避免 Stape 标签构造无效 contents
  };
}

function convertPlaceOrderPagePlaceOrderResult(_params: StapeTrackParams): GA4Payload {
  const currency = JSON.parse(localStorage.getItem('voo-currency') || '{}').name || 'USD';
  return {
    event: 'begin_checkout',
    currency,
    // begin_checkout 不关联具体商品，不传 items，避免 Stape 标签构造无效 contents
  };
}

// function convertPlaceOrderPagePlaceOrderResult(params: StapeTrackParams): GA4Payload | null {
//   if (!params.is_create_success) return null;
//   return {
//     event: 'add_payment_info',
//     currency: String(params.currency ?? ''),
//     value: Number(params.order_amount ?? 0),
//     payment_type: String(params.pay_type ?? ''),
//   };
// }

function convertPurchase(data: any): GA4Payload {
  return {
    event: 'purchase',
    ...data,
  };
}

function convertLoginSuccessNewResult(params: StapeTrackParams): GA4Payload {
  return {
    event: 'sign_up',
    method: String(params.login_method ?? ''),
  };
}

function convertLoginSuccessResult(params: StapeTrackParams): GA4Payload {
  return {
    event: 'login',
    method: String(params.login_type ?? ''),
  };
}

function convertPageView(_params: StapeTrackParams): GA4Payload {
  return {
    event: 'page_view',
  };
}

// ─── 主分发函数 ───────────────────────────────────────────────────────────────

const GA4_CONVERTERS: Record<string, (p: StapeTrackParams) => GA4Payload | null> = {
  IsReturn_Result: convertSearchBtnClick,
  ProductDetailsPage_View: convertProductDetailsPageView,
  ProductDetailsPageBtn_Click: convertProductDetailsPageBtnClick,
  AddToCart_Result: convertAddToCartBtnClick,
  CollectBtn_Click: convertCollectBtnClick,
  PlaceOrderPage_View: convertPlaceOrderPageView,
  PlaceOrderPagePlaceOrder_Result: convertPlaceOrderPagePlaceOrderResult,
  Purchase: convertPurchase,
  LoginSuccessNew_Result: convertLoginSuccessNewResult,
  LoginSuccess_Result: convertLoginSuccessResult,
  PageView: convertPageView,
};

function convertToGA4Payload(eventName: string, params: StapeTrackParams): GA4Payload | null {
  return GA4_CONVERTERS[eventName]?.(params) ?? null;
}

// ─── 商品数据校验 ─────────────────────────────────────────────────────────────

/**
 * 判断 GA4 items 是否为空
 * - items 不存在或为空数组 → 视为空
 * - 所有 item 的 item_id 和 item_product_id 均为空 → 视为空
 */
function isItemsEmpty(items: GA4Item[] | undefined): boolean {
  if (!items || items.length === 0) return true;
  return items.every((item) => !item.item_id && !item.item_product_id);
}

// ─── 主入口 ───────────────────────────────────────────────────────────────────

/**
 * 生成唯一事件 ID，格式为 {毫秒时间戳}_{9位随机整数}
 * 与 data-client 服务端保持一致，用于 GA4/Stape 事件去重
 */
export function generateEventId(): string {
  const random = Math.floor(Math.random() * 900000000) + 100000000;
  return `${Date.now()}_${random}`;
}

/**
 * 将神策事件转换为 GA4 规范参数后推送至 stapeDataLayer
 * 由 SDUtils.sensorsTrack 统一调用，与神策上报同步执行
 * 原神策参数会被完全丢弃，仅保留经 GA4 转换后的字段
 *
 * @param eventName - 神策事件名称
 * @param params - 经过空值过滤后的事件参数
 */
export function stapeTrack(eventName: string, params: StapeTrackParams): void {
  try {
    if (!window.stapeDataLayer) return;
    if (!isAllowedStapeEvent(eventName, params)) return;
    const payload = convertToGA4Payload(eventName, params);
    if (!payload) return;

    // 涉及商品上报的事件，若商品数据为空则取消上报
    if (payload.items && isItemsEmpty(payload.items)) return;
    const { event, ...ga4Params } = payload;
    window.stapeDataLayer.push({
      event,
      event_id: params.event_id || generateEventId(),
      user_data: getStapeUserData(),
      ...ga4Params,
      // 如果 payload 中不含 items，显式清除 dataLayer 继承的 items
      ...(!('items' in payload) && { items: undefined }),
    });
  } catch (e) {
    console.log('stapeTrack error', e);
  }
}
